import { type AnchorHTMLAttributes, type ButtonHTMLAttributes } from "react";
import { cn } from "@/lib/utils";

type ButtonVariant =
    | "yellow-1"
    | "yellow-2"
    | "yellow-3"
    | "gray"
    | "dark-1"
    | "dark-2"
    | "dark-3"
    | "dark-4"
    | "cta";

type ButtonBaseProps = {
    variant?: ButtonVariant;
    className?: string;
};

type ButtonAsButton = ButtonBaseProps &
    ButtonHTMLAttributes<HTMLButtonElement> & { href?: undefined };

type ButtonAsLink = ButtonBaseProps &
    AnchorHTMLAttributes<HTMLAnchorElement> & { href: string };

type ButtonProps = ButtonAsButton | ButtonAsLink;

const variantStyles: Record<ButtonVariant, string> = {
    "yellow-1": "bg-yellow-1 text-ink hover:bg-yellow-1-hover focus-visible:ring-yellow-1",
    "yellow-2": "bg-yellow-2 text-ink hover:bg-yellow-2-hover focus-visible:ring-yellow-2",
    "yellow-3": "bg-yellow-3 text-ink hover:bg-yellow-3-hover focus-visible:ring-yellow-3",
    gray: "bg-btn-disabled text-btn-disabled-fg hover:bg-gray-hover focus-visible:ring-btn-disabled",
    "dark-1": "bg-dark-btn-1 text-light hover:bg-dark-btn-1-hover focus-visible:ring-light",
    "dark-2": "bg-dark-btn-2 text-light hover:bg-dark-btn-2-hover focus-visible:ring-light",
    "dark-3": "bg-dark-btn-3 text-light hover:bg-dark-btn-3-hover focus-visible:ring-light",
    "dark-4": "bg-dark-btn-1 text-dark-fg hover:bg-dark-btn-1-hover focus-visible:ring-dark-fg",
    cta: "bg-light text-ink hover:bg-cta-hover focus-visible:ring-ink",
};

const baseStyles =
    "inline-flex cursor-pointer items-center justify-center gap-2 rounded-[10px] px-4 py-3 text-base font-medium transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2";

export function Button({ variant = "yellow-1", className, ...props }: ButtonProps) {
    const classes = cn(baseStyles, variantStyles[variant], className);

    if (props.href !== undefined) {
        return <a className={classes} {...(props as AnchorHTMLAttributes<HTMLAnchorElement>)} />;
    }

    return (
        <button
            type="button"
            className={classes}
            {...(props as ButtonHTMLAttributes<HTMLButtonElement>)}
        />
    );
}
